import type { ApiClient, Schemas } from '@argzombie/api-client';
import { ok } from './api';
import type { BeatChainDef } from './story/types';

/**
 * The Stories & Tech Step 1 admin calls, all through the typed `@argzombie/api-client` (S1-ENG and S1-SCN merged).
 *
 * | Function | Endpoint | Owner | State |
 * |---|---|---|---|
 * | `simulateBeats` | `POST /admin/beats/simulate` | ENG | typed |
 * | `queueDevBeat` | `POST /dev/beats/queue` | ENG | typed |
 * | `playerLedger` | `GET /admin/players/{id}/ledger` | ENG | typed |
 * | `voidLedgerRow` | `POST /admin/ledger/{id}/void` | ENG | typed |
 * | `playerAllies` | `GET /admin/players/{id}/allies` | ENG | typed |
 * | `endAlly` | `POST /admin/allies/{id}/end` | ENG | typed |
 * | `playerWorldFlags` | `GET /admin/players/{id}/world-flags` | SCN | typed |
 * | `resetWorldFlags` | `POST /admin/players/{id}/world-flags/reset` | SCN | typed |
 * | `playerMissions` | `GET /admin/players/{id}/missions` | SCN | typed |
 * | `resetMission` | `POST /admin/players/{id}/missions/{missionId}/reset` | SCN | typed |
 * | `previewSetPiece` | `POST /admin/set-pieces/preview` | SCN | typed |
 */

// ----- simulate and queue (engine.html §5 "Simulate rolls"), S1-ENG -------------------------------------------------------------

export type SimulateRequest = {
  /** The draft chain, as edited: it replaces any chain with its id in the current content, then the real BeatRoller runs. */
  chain: BeatChainDef;
  /** Tickets to roll (1..10000): 1 for "Roll a ticket", 1000 for "Simulate 1000 tickets". */
  n: number;
  seed: number;
  /** Pinned hidden vars or tells (`companion.bitten` → `true`). */
  pin: Record<string, string>;
  /** Server facts to try (`team.size` → `3`, `ledger.tag(killed_innocent)` → `true`). */
  context: Record<string, string>;
};
export type SimulateResult = Schemas['StorySimulateResponse'];
export type StorySimulation = Schemas['StorySimulation'];

export const simulateBeats = async (client: ApiClient, req: SimulateRequest) =>
  ok(await client.POST('/admin/beats/simulate', { body: req as Schemas['StorySimulateRequest'] }));

export const queueDevBeat = async (
  client: ApiClient,
  req: { chainId: string; pin: Record<string, string> },
) => ok(await client.POST('/dev/beats/queue', { body: req }));

// ----- the player's story: ledger, allies, world flags, mission progress -------------------------------------------------------

/** One `choice_ledger` row (engine.html §5 "The choice_ledger table"). */
export type LedgerRow = Schemas['StoryLedgerRow'];

/** The player's ledger rows, newest first (the API pages them: 100 by default, at most 500). */
export const playerLedger = async (client: ApiClient, playerId: string, take = 200) =>
  ok(
    await client.GET('/admin/players/{id}/ledger', {
      params: { path: { id: playerId }, query: { take } },
    }),
  ).items;

export const voidLedgerRow = async (client: ApiClient, rowId: string, reason: string) =>
  ok(
    await client.POST('/admin/ledger/{id}/void', {
      params: { path: { id: rowId } },
      body: { reason },
    }),
  );

export type AllyDeliveryRow = Schemas['StoryAdminDeliveryRow'];
/** One `story_allies` row with its deliveries. */
export type AllyRow = Schemas['StoryAdminAllyRow'];

export const playerAllies = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/allies', { params: { path: { id: playerId } } }));

export const endAlly = async (client: ApiClient, allyRowId: string, reason: string) =>
  ok(
    await client.POST('/admin/allies/{id}/end', {
      params: { path: { id: allyRowId } },
      body: { reason },
    }),
  );

// ----- the scenario layer (S1-SCN) ----------------------------------------------------------------------------------------------

/** One `player_world_flags` row: `scopeKey` is the bare run id or `account`, `scope` is `run` or `account`. */
export type WorldFlagRow = Schemas['AdminWorldFlagRow'];

export const playerWorldFlags = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/world-flags', { params: { path: { id: playerId } } }));

/** Clears one flag (`key`) or every flag in a scope (`scope: run | account`): exactly one of them (else 400 invalid_reset). */
export const resetWorldFlags = async (
  client: ApiClient,
  playerId: string,
  body: { reason: string; key?: string; scope?: string },
) =>
  ok(
    await client.POST('/admin/players/{id}/world-flags/reset', {
      params: { path: { id: playerId } },
      body,
    }),
  );

/** One `mission_progress` row with the stage machine and its log (scenario.html §5). */
export type MissionProgressRow = Schemas['AdminMissionProgressRow'];

export const playerMissions = async (client: ApiClient, playerId: string) =>
  ok(await client.GET('/admin/players/{id}/missions', { params: { path: { id: playerId } } }));

export const resetMission = async (
  client: ApiClient,
  playerId: string,
  missionId: string,
  reason: string,
) =>
  ok(
    await client.POST('/admin/players/{id}/missions/{missionId}/reset', {
      params: { path: { id: playerId, missionId } },
      body: { reason },
    }),
  );

export type SetPiecePreview = Schemas['AdminSetPiecePreview'];
export type SetPiecePreviewResult = Schemas['AdminSetPiecePreviewResult'];

/** The placement preview: an SVG per seed, the 500-seed sweep, and the set piece's validator errors. */
export const previewSetPiece = async (
  client: ApiClient,
  body: {
    setPiece: unknown;
    seeds: number[];
    category?: string;
    placement?: string;
    doorPolicy?: string;
    minRooms?: number;
  },
) =>
  ok(
    await client.POST('/admin/set-pieces/preview', {
      body: { minRooms: 0, ...body } as Schemas['AdminSetPiecePreviewRequest'],
    }),
  );
